<template>
  <nav
    class="fixed z-50 flex h-[64px] w-full items-center justify-center backdrop-blur md:h-[72px]"
    :class="isBusinessServicesPage ? 'bg-[#074734]' : 'bg-white/80'">
    <div class="container flex items-center justify-between gap-2">
      <!-- Logo and navigation links -->
      <div class="flex items-center gap-1 md:gap-4 xl:gap-12">
        <!-- Mobile hamburger menu -->
        <div class="xl:hidden">
          <button
            class="flex items-center justify-center transition-colors"
            :class="isBusinessServicesPage ? 'text-white hover:text-green-400' : 'text-gray-600 hover:text-green-600'"
            @click="isMobileMenuOpen = !isMobileMenuOpen">
            <svg
              class="size-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              :class="{ 'rotate-90': isMobileMenuOpen }">
              <path
                v-if="!isMobileMenuOpen"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16" />
              <path
                v-else
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <NuxtLink
          to="/"
          class="flex">
          <Logo
            v-if="!isBusinessServicesPage"
            class="h-6 self-center lg:h-8" />
          <img
            v-else
            src="/icons/logo-business.svg"
            alt="PicUp Business"
            class="h-6 self-center lg:h-8">
        </NuxtLink>
        <div
          v-if="showHomepageLinks"
          class="font-display flex gap-8 self-center font-medium uppercase max-xl:hidden">
          <!-- Services link with dropdown for location pages -->
          <div
            v-if="isLocationPage"
            class="group relative flex">
            <button class="flex items-center gap-1 uppercase text-gray-400 transition-colors hover:text-green-600">
              Services
              <svg
                class="size-4 transition-transform group-hover:rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div class="invisible absolute left-0 top-full z-50 mt-2 w-48 rounded-lg border border-gray-200 bg-white opacity-0 shadow-lg transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <div class="py-2">
                <NuxtLink
                  :to="`${currentLocationPath}/removals`"
                  class="block px-4 py-2 text-sm uppercase text-gray-700 transition-colors hover:bg-gray-100 hover:text-green-600">
                  Removals
                </NuxtLink>
                <NuxtLink
                  :to="`${currentLocationPath}/small-moves`"
                  class="block px-4 py-2 text-sm uppercase text-gray-700 transition-colors hover:bg-gray-100 hover:text-green-600">
                  Small Moves & Delivery
                </NuxtLink>
                <NuxtLink
                  :to="`${currentLocationPath}/junk-removal`"
                  class="block px-4 py-2 text-sm uppercase text-gray-700 transition-colors hover:bg-gray-100 hover:text-green-600">
                  Junk Removal
                </NuxtLink>
              </div>
            </div>
          </div>
          <!-- Regular Services link for homepage -->
          <NuxtLink
            v-else
            to="/#services"
            class="self-center transition-colors"
            :class="isBusinessServicesPage ? 'text-white hover:text-green-400' : 'text-gray-400 hover:text-green-600'">
            Services
          </NuxtLink>

          <div class="group relative flex">
            <NuxtLink
              to="/business-services/last-mile-delivery"
              class="flex items-center gap-1 uppercase transition-colors"
              :class="isBusinessServicesPage ? 'text-white hover:text-green-400' : 'text-gray-400 hover:text-green-600'">
              Business
              <svg
                class="size-4 transition-transform group-hover:rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7" />
              </svg>
            </NuxtLink>
            <div class="invisible absolute left-0 top-full z-50 mt-2 w-64 rounded-lg border border-gray-200 bg-white opacity-0 shadow-lg transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <div class="py-2">
                <NuxtLink
                  to="/business-services/last-mile-delivery"
                  class="block px-4 py-2 text-sm uppercase text-gray-700 transition-colors hover:bg-gray-100 hover:text-green-600">
                  Last-Mile Delivery
                </NuxtLink>
              </div>
            </div>
          </div>
          <NuxtLink
            to="/#apply-to-drive"
            class="self-center transition-colors"
            :class="isBusinessServicesPage ? 'text-white hover:text-green-400' : 'text-gray-400 hover:text-green-600'">
            Apply to Drive
          </NuxtLink>

          <!-- Locations dropdown for homepage only -->
          <div
            class="group relative flex">
            <button
              class="flex items-center gap-1 uppercase transition-colors"
              :class="isBusinessServicesPage ? 'text-white hover:text-green-400' : 'text-gray-400 hover:text-green-600'">
              Locations
              <svg
                class="size-4 transition-transform group-hover:rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div class="invisible absolute left-0 top-full z-50 mt-2 w-64 rounded-lg border border-gray-200 bg-white opacity-0 shadow-lg transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <div class="py-2">
                <!-- Brisbane -->
                <div class="group/brisbane">
                  <NuxtLink
                    to="/brisbane"
                    class="flex items-center justify-between px-4 py-2 text-sm uppercase text-gray-700 transition-colors hover:bg-gray-100 hover:text-green-600">
                    Brisbane
                    <svg
                      class="size-3 transition-transform group-hover/brisbane:rotate-90"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7" />
                    </svg>
                  </NuxtLink>
                  <div class="invisible absolute left-full top-0 ml-1 w-48 rounded-lg border border-gray-200 bg-white opacity-0 shadow-lg transition-all duration-200 group-hover/brisbane:visible group-hover/brisbane:opacity-100">
                    <div class="py-2">
                      <NuxtLink
                        to="/brisbane/removals"
                        class="block px-4 py-2 text-sm uppercase text-gray-700 transition-colors hover:bg-gray-100 hover:text-green-600">
                        Removals
                      </NuxtLink>
                      <NuxtLink
                        to="/brisbane/small-moves"
                        class="block px-4 py-2 text-sm uppercase text-gray-700 transition-colors hover:bg-gray-100 hover:text-green-600">
                        Small Moves & Delivery
                      </NuxtLink>
                      <NuxtLink
                        to="/brisbane/junk-removal"
                        class="block px-4 py-2 text-sm uppercase text-gray-700 transition-colors hover:bg-gray-100 hover:text-green-600">
                        Junk Removal
                      </NuxtLink>
                    </div>
                  </div>
                </div>

                <!-- Gold Coast -->
                <div class="group/goldcoast">
                  <NuxtLink
                    to="/gold-coast"
                    class="flex items-center justify-between px-4 py-2 text-sm uppercase text-gray-700 transition-colors hover:bg-gray-100 hover:text-green-600">
                    Gold Coast
                    <svg
                      class="size-3 transition-transform group-hover/goldcoast:rotate-90"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7" />
                    </svg>
                  </NuxtLink>
                  <div class="invisible absolute left-full top-0 ml-1 w-48 rounded-lg border border-gray-200 bg-white opacity-0 shadow-lg transition-all duration-200 group-hover/goldcoast:visible group-hover/goldcoast:opacity-100">
                    <div class="py-2">
                      <NuxtLink
                        to="/gold-coast/removals"
                        class="block px-4 py-2 text-sm uppercase text-gray-700 transition-colors hover:bg-gray-100 hover:text-green-600">
                        Removals
                      </NuxtLink>
                      <NuxtLink
                        to="/gold-coast/small-moves"
                        class="block px-4 py-2 text-sm uppercase text-gray-700 transition-colors hover:bg-gray-100 hover:text-green-600">
                        Small Moves & Delivery
                      </NuxtLink>
                      <NuxtLink
                        to="/gold-coast/junk-removal"
                        class="block px-4 py-2 text-sm uppercase text-gray-700 transition-colors hover:bg-gray-100 hover:text-green-600">
                        Junk Removal
                      </NuxtLink>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <NuxtLink
            to="/blog"
            class="flex items-center gap-1 uppercase transition-colors"
            :class="isBusinessServicesPage ? 'text-white hover:text-green-400' : 'text-gray-400 hover:text-green-600'">
            Blog
          </NuxtLink>
        </div>
      </div>

      <div class="flex gap-1 lg:gap-3">
        <div class="flex flex-col items-end justify-center">
          <PhoneNumber
            class="flex items-center gap-1 text-sm"
            :color="isBusinessServicesPage ? 'text-white' : ''" />
        </div>

        <div
          v-if="!user"
          class="content-center">
          <Button
            color="clear"
            class="max-sm:text-sm"
            :class="isBusinessServicesPage ? 'text-white' : ''"
            @click="showLoginModal = true">
            Sign in
          </Button>
        </div>

        <NuxtLink
          v-if="user"
          to="/account"
          class="content-center">
          <Button
            color="dark"
            class="max-sm:h-8 max-sm:px-2 max-sm:text-sm"
            :class="isBusinessServicesPage ? 'bg-white !text-[#074734] !hover:text-white' : ''">
            Account
          </Button>
        </NuxtLink>

        <NuxtLink
          v-if="showHomepageLinks"
          to="/bookings"
          class="self-center">
          <Button
            class="max-sm:h-8 max-sm:px-2"
            :class="isBusinessServicesPage ? '!bg-[#CDF07B] !text-[#084734] hover:!bg-[#B8E066]' : ''">
            <span class="max-sm:text-sm">
              <span class="max-sm:hidden">Get a</span>
              Quote
            </span>
          </Button>
        </NuxtLink>
      </div>
    </div>
  </nav>

  <!-- Mobile menu overlay - positioned outside nav to cover full viewport -->
  <div
    v-if="isMobileMenuOpen"
    class="fixed inset-0 z-[60] bg-black/50 md:hidden"
    @click="isMobileMenuOpen = false" />

  <!-- Mobile menu sidebar - positioned outside nav to cover full viewport -->
  <div
    class="fixed left-0 top-0 z-[70] h-screen w-80 bg-white shadow-xl transition-transform duration-300 ease-in-out md:hidden"
    :class="{ 'translate-x-0': isMobileMenuOpen, '-translate-x-full': !isMobileMenuOpen }">
    <div class="flex h-full flex-col">
      <!-- Mobile menu header -->
      <div class="flex items-center justify-between border-b border-gray-200 p-4">
        <Logo
          class="h-6" />
        <button
          class="flex size-8 items-center justify-center text-gray-600 transition-colors"
          @click="isMobileMenuOpen = false">
          <svg
            class="size-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Mobile menu content -->
      <div
        v-if="showHomepageLinks"
        class="flex-1 overflow-y-auto p-4">
        <div class="space-y-4">
          <!-- Mobile CTA -->
          <NuxtLink
            to="/bookings"
            class="block w-full rounded-lg bg-green-600 px-4 py-3 text-center font-medium uppercase text-white transition-colors hover:bg-green-700"
            @click="isMobileMenuOpen = false">
            Book Now
          </NuxtLink>

          <!-- Services Accordion -->
          <div class="border-b border-gray-100 pb-3">
            <button
              class="flex w-full items-center justify-between py-3 text-left font-medium text-gray-700 transition-colors hover:text-green-600"
              @click="toggleAccordion('services')">
              <span class="font-bold">Services</span>
              <svg
                class="size-5 transition-transform"
                :class="{ 'rotate-180': mobileAccordionState.services }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              v-show="mobileAccordionState.services"
              class="space-y-2 pl-4 pt-2 transition-all duration-200 ease-in-out">
              <NuxtLink
                v-if="isLocationPage"
                :to="`${currentLocationPath}/removals`"
                class="block py-2 text-gray-600 transition-colors hover:text-green-600"
                @click="isMobileMenuOpen = false">
                Removals
              </NuxtLink>
              <NuxtLink
                v-if="isLocationPage"
                :to="`${currentLocationPath}/small-moves`"
                class="block py-2 text-gray-600 transition-colors hover:text-green-600"
                @click="isMobileMenuOpen = false">
                Small Moves & Delivery
              </NuxtLink>
              <NuxtLink
                v-if="isLocationPage"
                :to="`${currentLocationPath}/junk-removal`"
                class="block py-2 text-gray-600 transition-colors hover:text-green-600"
                @click="isMobileMenuOpen = false">
                Junk Removal
              </NuxtLink>
              <div v-else>
                <NuxtLink
                  to="#services"
                  class="block py-2 text-gray-600 transition-colors hover:text-green-600"
                  @click="isMobileMenuOpen = false">
                  Removals
                </NuxtLink>
                <NuxtLink
                  to="#services"
                  class="block py-2 text-gray-600 transition-colors hover:text-green-600"
                  @click="isMobileMenuOpen = false">
                  Small Moves & Delivery
                </NuxtLink>
                <NuxtLink
                  to="#services"
                  class="block py-2 text-gray-600 transition-colors hover:text-green-600"
                  @click="isMobileMenuOpen = false">
                  Junk Removal
                </NuxtLink>
              </div>
            </div>
          </div>

          <!-- Business Accordion -->
          <div class="border-b border-gray-100 pb-3">
            <button
              class="flex w-full items-center justify-between py-3 text-left font-medium text-gray-700 transition-colors hover:text-green-600"
              @click="toggleAccordion('business')">
              <span class="font-bold">Businesses</span>
              <svg
                class="size-5 transition-transform"
                :class="{ 'rotate-180': mobileAccordionState.business }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              v-show="mobileAccordionState.business"
              class="space-y-2 pl-4 pt-2 transition-all duration-200 ease-in-out">
              <NuxtLink
                to="/business-services/last-mile-delivery"
                class="block py-2 text-gray-600 transition-colors hover:text-green-600"
                @click="isMobileMenuOpen = false">
                Last-Mile Delivery
              </NuxtLink>
            </div>
          </div>

          <!-- Locations Accordion -->
          <div class="border-b border-gray-100 pb-3">
            <button
              class="flex w-full items-center justify-between py-3 text-left font-medium text-gray-700 transition-colors hover:text-green-600"
              @click="toggleAccordion('locations')">
              <span class="font-bold">Locations</span>
              <svg
                class="size-5 transition-transform"
                :class="{ 'rotate-180': mobileAccordionState.locations }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              v-show="mobileAccordionState.locations"
              class="space-y-3 pl-4 pt-2 transition-all duration-200 ease-in-out">
              <!-- Brisbane -->
              <div class="space-y-2">
                <NuxtLink
                  to="/brisbane"
                  class="block py-2 font-medium text-gray-700 transition-colors hover:text-green-600"
                  @click="isMobileMenuOpen = false">
                  Brisbane
                </NuxtLink>
                <div class="space-y-1 pl-4">
                  <NuxtLink
                    to="/brisbane/removals"
                    class="block py-1 text-sm text-gray-600 transition-colors hover:text-green-600"
                    @click="isMobileMenuOpen = false">
                    Removals
                  </NuxtLink>
                  <NuxtLink
                    to="/brisbane/small-moves"
                    class="block py-1 text-sm text-gray-600 transition-colors hover:text-green-600"
                    @click="isMobileMenuOpen = false">
                    Small Moves & Delivery
                  </NuxtLink>
                  <NuxtLink
                    to="/brisbane/junk-removal"
                    class="block py-1 text-sm text-gray-600 transition-colors hover:text-green-600"
                    @click="isMobileMenuOpen = false">
                    Junk Removal
                  </NuxtLink>
                </div>
              </div>

              <!-- Gold Coast -->
              <div class="space-y-2">
                <NuxtLink
                  to="/gold-coast"
                  class="block py-2 font-medium text-gray-700 transition-colors hover:text-green-600"
                  @click="isMobileMenuOpen = false">
                  Gold Coast
                </NuxtLink>
                <div class="space-y-1 pl-4">
                  <NuxtLink
                    to="/gold-coast/removals"
                    class="block py-1 text-sm text-gray-600 transition-colors hover:text-green-600"
                    @click="isMobileMenuOpen = false">
                    Removals
                  </NuxtLink>
                  <NuxtLink
                    to="/gold-coast/small-moves"
                    class="block py-1 text-sm text-gray-600 transition-colors hover:text-green-600"
                    @click="isMobileMenuOpen = false">
                    Small Moves & Delivery
                  </NuxtLink>
                  <NuxtLink
                    to="/gold-coast/junk-removal"
                    class="block py-1 text-sm text-gray-600 transition-colors hover:text-green-600"
                    @click="isMobileMenuOpen = false">
                    Junk Removal
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>

          <!-- Drive with PicUp Accordion -->
          <div class="border-b border-gray-100 pb-3">
            <button
              class="flex w-full items-center justify-between py-3 text-left font-medium text-gray-700 transition-colors hover:text-green-600"
              @click="toggleAccordion('drive')">
              <span class="font-bold">Drive with PicUp</span>
              <svg
                class="size-5 transition-transform"
                :class="{ 'rotate-180': mobileAccordionState.drive }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              v-show="mobileAccordionState.drive"
              class="space-y-2 pl-4 pt-2 transition-all duration-200 ease-in-out">
              <NuxtLink
                to="/#apply-to-drive"
                class="block py-2 text-gray-600 transition-colors hover:text-green-600"
                @click="isMobileMenuOpen = false">
                Apply to Drive
              </NuxtLink>
            </div>
          </div>

          <NuxtLink
            to="/blog"
            class="block py-2 font-bold text-gray-700 transition-colors hover:text-green-600">
            Blog
          </NuxtLink>

          <!-- Phone number and contact info -->
          <div class="border-t border-gray-200 pt-4">
            <div class="space-y-3">
              <!-- Phone number -->
              <div class="flex items-center justify-center">
                <div class="flex items-center gap-2">
                  <Icon
                    name="heroicons:phone-solid"
                    class="size-5 text-gray-600" />
                  <PhoneNumber class="flex items-center gap-1 text-lg font-semibold" />
                </div>
              </div>

              <!-- Sign in/Account section -->
              <div class="space-y-2">
                <div
                  v-if="!user"
                  class="flex justify-center">
                  <Button
                    color="clear"
                    class="w-full justify-center"
                    @click="showLoginModal = true; isMobileMenuOpen = false">
                    Sign in
                  </Button>
                </div>

                <NuxtLink
                  v-if="user"
                  to="/account"
                  class="flex justify-center"
                  @click="isMobileMenuOpen = false">
                  <Button
                    color="dark"
                    class="w-full justify-center">
                    Account
                  </Button>
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { User } from '@supabase/supabase-js'

defineProps<{
  user?: User | null
}>()

const router = useRouter()
const showLoginModal = useState('showLoginModal', () => false)
const isMobileMenuOpen = ref(false)

// Accordion state for mobile menu
const mobileAccordionState = ref({
  services: false,
  business: false,
  locations: false,
  drive: false,
})

const showHomepageLinks = computed(() => {
  const routeName = router.currentRoute.value.name
  const routePath = router.currentRoute.value.path

  // Show on homepage, location pages, or business services pages
  return routeName === 'index'
    || routePath.startsWith('/brisbane')
    || routePath.startsWith('/gold-coast')
    || routePath.startsWith('/sydney')
    || routePath.startsWith('/blog')
    || routePath.startsWith('/business-services')
})

const isBusinessServicesPage = computed(() => {
  const routePath = router.currentRoute.value.path
  return routePath.startsWith('/business-services')
})

const isLocationPage = computed(() => {
  const routePath = router.currentRoute.value.path
  return routePath.startsWith('/brisbane')
    || routePath.startsWith('/gold-coast')
    || routePath.startsWith('/sydney')
})

const currentLocationPath = computed(() => {
  const routePath = router.currentRoute.value.path
  if (routePath.startsWith('/brisbane')) return '/brisbane'
  if (routePath.startsWith('/gold-coast')) return '/gold-coast'
  if (routePath.startsWith('/sydney')) return '/sydney'
  return ''
})

// Toggle accordion section
const toggleAccordion = (section: keyof typeof mobileAccordionState.value) => {
  mobileAccordionState.value[section] = !mobileAccordionState.value[section]
}

// Close mobile menu when route changes
watch(() => router.currentRoute.value.path, () => {
  isMobileMenuOpen.value = false
})
</script>
